import generated from '@/lib/i18n/disclaimers.generated.json';

/**
 * ARCHITECTURE §15.4 免責聲明文字 —— 前端側的**唯一**入口。
 *
 * 文字本身不住在這裡。它住在 `backend/app/llm/fallbacks/disclaimers.py`，
 * 由 `python -m core.lint.export` 產生 `src/lib/i18n/disclaimers.generated.json`，
 * 這個模組只負責把產生物包成型別安全的形狀。
 *
 * 為什麼是「產生」而不是在 TS 這邊再抄一份：§15.4 的文字是**法律操作文字**
 * （投信投顧法 §4／§107），而它同時要被三個地方用到——LLM fallback 樣板、
 * content lint 的豁免判斷、以及 <ModelOutput> 的 footer。抄第二份的那一天，
 * 它就開始漂移，而漂移的方向永遠是「讀起來比較順」。
 * `python -m core.lint.export --check` 在 CI 會判定產生物是否過期。
 *
 * 它們刻意**不**走 i18n 查表：i18n 查不到 key 時會靜默回傳 key 本身，
 * 那等於一個沒有揭露的模型輸出。這幾句話一律以繁體中文原文渲染，與介面語言無關——
 * 台灣主管機關讀的就是這份中文。
 */
const SOURCE = generated.disclaimer;

export const DISCLAIMERS = {
  /** 全域頁尾（每一頁） */
  globalFooter: SOURCE.global_footer,
  /** 模型輸出 footer 的**樣板**，含 {model_id} / {as_of} / {ic}。用 renderModelOutputFooter() 展開。 */
  modelOutput: SOURCE.model_output,
  /** 回測結果（AssumptionsPanel 上方，P4） */
  backtest: SOURCE.backtest,
  /** 風險輪廓（問卷開始前與結果頁，P6） */
  riskProfile: SOURCE.risk_profile,
  /** AI 對話介面（/profile/risk/conversation，P7） */
  aiConversation: SOURCE.ai_conversation,
  /** 右下角站內說明助理（ADR-0003，面板內常駐） */
  siteAssistant: SOURCE.site_assistant,
  /** 庫存股（/:market/portfolio 頁尾，P11） */
  portfolio: SOURCE.portfolio,
} as const;

export type DisclaimerKey = keyof typeof DISCLAIMERS;

/** 可以整段直接渲染的揭露。modelOutput 是樣板，不在其中——型別上就排除掉。 */
export type StaticDisclaimerKey = Exclude<DisclaimerKey, 'modelOutput'>;

export const DISCLAIMER_SOURCE = generated._source;

export interface ModelOutputFooterValues {
  model_id: string;
  as_of: string;
  ic: string;
}

const PLACEHOLDER = /\{(\w+)\}/g;

/**
 * 把 §15.4 的 model_output 樣板展開成逐行文字。
 *
 * 回傳陣列而不是單一字串，是為了讓樣板行數改變時前端不需要跟著改：
 * 呼叫端逐行渲染即可。未知的 placeholder 直接 throw——
 * 畫面上出現一個字面的 `{ic}` 比崩潰更糟，它看起來像 UI bug 而不是法遵漏洞。
 */
export function renderModelOutputFooter(values: ModelOutputFooterValues): string[] {
  return DISCLAIMERS.modelOutput.split('\n').map((line) =>
    line.replace(PLACEHOLDER, (_match, name: string) => {
      if (name === 'model_id') return values.model_id;
      if (name === 'as_of') return values.as_of;
      if (name === 'ic') return values.ic;
      throw new Error(
        `§15.4 model_output 樣板出現未知的 placeholder {${name}}。` +
          '樣板由 backend/app/llm/fallbacks/disclaimers.py 產生；' +
          '新增欄位時必須同步更新 renderModelOutputFooter()。',
      );
    }),
  );
}
